import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { IDUtil } from "./id-util";
import { dropDowns } from "../shared/drop-downs";
import {
  CANDADIAN_DRIVER_LICENCE_ID,
  DpBooleanValueTypes,
  PASSPORT_CANADIAN_ID,
  UploadedFileDescription,
} from "../shared/constants";
import moment from "moment";
import { ElementRef } from "@angular/core";

//words that should remain as upper case
const upperCaseWords: string[] = [
  "PO",
  "P.O.",
  "RR",
  "R.R.",
  "NE",
  "NW",
  "SE",
  "SW",
];

//words that should be converted to lower case
const lowerCaseWords: string[] = ["AND", "OF"];

export class Utils {
  public static EMAIL_ADDRESS_PATTERN: RegExp =
    /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

  constructor() {}

  static uuid(): string {
    //let uidStr = UUID.UUID();
    let uidStr = IDUtil.getID();
    if (uidStr) {
      uidStr = uidStr.replace(/\D/g, "");
    }
    return uidStr;
  }

  static getBrowserInfo(): string {
    return window.navigator.userAgent;
  }

  static invokeFieldLevelValidation(formGroup: FormGroup): void {
    formGroup.markAllAsTouched();
  }

  static updateFormGroupRequiredAttributesForClient(
    form: FormGroup,
    requiredAttribute: boolean,
    emailPattern: RegExp,
    onlySelf?: boolean
  ): void {
    [
      "email",
      "residentStatus",
      "isFamilyResidence",
      "isSpouseOnTitle",
      "identifications",
    ].forEach((key) => {
      if (form && form.get(key)) {
        if (key == "email") {
          this.updateEmailRequiredAttribute(
            form,
            requiredAttribute,
            emailPattern,
            onlySelf
          );
        } else if (key == "identifications") {
          this.updateIdentificationsRequiredAttribute(
            form,
            requiredAttribute,
            onlySelf
          );
        } else {
          if (requiredAttribute && this.isFieldVisible(form, key)) {
            //Used to avoid adding the required validation to the hidden fields
            form.get(key).setValidators([Validators.required]);
          } else {
            form.get(key).clearValidators();
          }
          form.get(key).updateValueAndValidity({
            onlySelf: !!onlySelf,
          });
        }
      }
    });
  }

  static isFieldVisible(form: FormGroup, key: string): boolean {
    if (key == "residentStatus") {
      return (
        form.get("isCanadianResident") &&
        form.get("isCanadianResident").value == DpBooleanValueTypes.YES
      );
    }
    if (key == "isFamilyResidence" || key == "isSpouseOnTitle") {
      return (
        form.get("spousalStatus") &&
        form.get("spousalStatus").value == "MARRIED"
      );
    }
  }

  static updateEmailRequiredAttribute(
    form: FormGroup,
    requiredAttribute: boolean,
    emailPattern: RegExp,
    onlySelf?: boolean
  ) {
    // Removing manual validation as it will be drived from the configuration
    // if (requiredAttribute) {
    //     form.get('email').setValidators([Validators.required, Validators.pattern(emailPattern)]);
    // } else {
    //     form.get('email').clearValidators();
    //     form.get('email').setValidators([Validators.pattern(emailPattern)]);
    // }
    // form.get('email').updateValueAndValidity({
    //     onlySelf: !!onlySelf
    // });
  }

  static updateIdentificationsRequiredAttribute(
    form: FormGroup,
    requiredAttribute: boolean,
    onlySelf?: boolean
  ) {
    this.updateIdentificationRequiredAttribute(
      <FormGroup>form.get("identifications").get("0"),
      requiredAttribute,
      onlySelf
    );
    this.updateIdentificationRequiredAttribute(
      <FormGroup>form.get("identifications").get("1"),
      requiredAttribute,
      onlySelf
    );
  }

  static updateIdentificationRequiredAttribute(
    identification: FormGroup,
    requiredAttribute: boolean,
    onlySelf?: boolean
  ) {
    if (requiredAttribute) {
      // Removing manual validation as it will be drived from the configuration
      // identification.get('identificationTypeCode').setValidators([Validators.required]);
      // identification.get('identificationNumber').setValidators([Validators.required]);
      // identification.get('placeOfIssue').setValidators([Validators.required]);
    } else {
      identification.get("identificationTypeCode").clearValidators();
      identification.get("identificationNumber").clearValidators();
      identification.get("placeOfIssue").clearValidators();
    }
    identification
      .get("expiryDate")
      .setValidators([Utils.datePickerValidator()]);

    identification.get("identificationTypeCode").updateValueAndValidity({
      onlySelf: !!onlySelf,
    });
    identification.get("identificationNumber").updateValueAndValidity({
      onlySelf: !!onlySelf,
    });
    identification.get("placeOfIssue").updateValueAndValidity({
      onlySelf: !!onlySelf,
    });
    identification.get("expiryDate").updateValueAndValidity({
      onlySelf: !!onlySelf,
    });
  }

  static updatePhoneValidationForClient(
    formGroup: FormGroup,
    requiredAttribute: boolean,
    onlySelf?: boolean
  ): void {
    if (
      formGroup &&
      formGroup.getRawValue().telephones &&
      formGroup.getRawValue().telephones.length > 2
    ) {
      let telephone = formGroup
        .getRawValue()
        .telephones.some((item) => !!item.telephoneNumber);
      formGroup.get("telephones")["controls"].forEach((obj) => {
        obj.controls.telephoneNumber.setValidators(
          telephone || !requiredAttribute ? null : Validators.required
        );
        obj.controls.telephoneNumber.updateValueAndValidity({
          onlySelf: !!onlySelf,
        });
      });
    }
  }

  static resetField(field: AbstractControl, initialValue: string): void {
    field.reset(initialValue);
    field.setValidators(null);
    field.updateValueAndValidity();
  }

  static setFieldAsRequired(field: AbstractControl): void {
    field.setValidators(Validators.required);
    field.updateValueAndValidity();
  }

  static truncateStringForDisplay(
    input: string,
    maxLength: number,
    suffix: string = ""
  ): string {
    if (!input) {
      return "";
    }
    if (input.length <= maxLength) {
      return input;
    }
    if (!suffix || suffix.length >= maxLength) {
      return input.substr(0, maxLength);
    }

    return input.substr(0, maxLength - suffix.length) + suffix;
  }

  static patternValidator(
    regex: RegExp,
    error: ValidationErrors,
    isReverse: boolean = false
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        // if control is empty return no error
        return null;
      }

      // test the value of the control against the regexp supplied
      const valid = regex.test(control.value);

      // if true, return no error (no error), else return error passed in the second parameter
      let returnItem: any;
      if (!!isReverse) {
        return !valid ? null : error;
      } else {
        return valid ? null : error;
      }
    };
  }

  static datePickerValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      //console.log(control.value.toString());
      let expiryDate = control.value;
      let isValid =
        !expiryDate ||
        (!!expiryDate &&
          (moment(expiryDate, "DD/MM/YYYY").isValid() ||
            Utils.checkValidPartialDate(expiryDate)));
      return isValid ? null : { invalidDate: true };
    };
  }

  static checkValidPartialDate(dateValue: string): boolean {
    return (
      !!dateValue &&
      (moment(dateValue, "D/M", true).isValid() ||
        moment(dateValue, "DD/MM", true).isValid() ||
        moment(dateValue, "MM", true).isValid() ||
        moment(dateValue, "MM/YYYY", true).isValid() ||
        moment(dateValue, "DD", true).isValid() ||
        moment(dateValue, "YYYY", true).isValid())
    );
  }

  static passwordMatchValidator(
    formGroup: FormGroup,
    error: ValidationErrors
  ): ValidatorFn {
    return this.fieldMatchValidator("password", formGroup, error);
  }

  static fieldMatchValidator(
    fieldName: string,
    formGroup: FormGroup,
    error: ValidationErrors
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      let field = formGroup.controls[fieldName].value;
      const retypeField: string = control.value; // get the value from the form control
      if (!field || !retypeField) {
        // if control is empty return no error
        return null;
      }

      // test the value of the control against the regexp supplied
      return field !== retypeField ? error : null;
    };
  }

  static getLabelFromValue(value: string, dropdownArray: any[]): string {
    let item = dropdownArray.find((row) => row.value == value);
    return item ? item.name : "";
  }

  static getMatterPurchasersCapacityOptions(
    numberOfPurchasers: number,
    provinceCode: string
  ): any[] {
    let options = dropDowns.matterPurchasersCapacityOptions.map(
      (option) => option
    );
    if (provinceCode == "MB" || provinceCode == "SK") {
      options = [
        ...options,
        ...[
          { name: "As joint tenants", value: "JOINT_TENANTS" },
          {
            name:
              "Each " + this.getDynamicValueInterestMbSK(numberOfPurchasers),
            value: "TENANTS_IN_COMMON_SPLIT_SHARES",
          },
        ],
      ];
      if (provinceCode == "SK") {
        options = [
          ...options,
          ...[
            {
              name: "Joint Tenants with no survivorship",
              value: "TENANTS_IN_COMMON_UNSPECIFIED_SHARES",
            },
          ],
        ];
      }
    } else {
      if (numberOfPurchasers == 1) {
        options.push({ name: "Registered owner", value: "REGISTERED_OWNER" });
      }
      if (numberOfPurchasers > 1) {
        options = [
          ...options,
          ...[
            { name: "As joint tenants", value: "JOINT_TENANTS" },
            {
              name:
                "As tenants in common (each " +
                this.getDynamicValueInterest(numberOfPurchasers) +
                ")",
              value: "TENANTS_IN_COMMON_SPLIT_SHARES",
            },
            {
              name: "As tenants in common (without specifying shares)",
              value: "TENANTS_IN_COMMON_UNSPECIFIED_SHARES",
            },
          ],
        ];
      }
    }

    options.push({ name: "Other", value: "OTHER" });
    return options;
  }

  static getDynamicValueInterestMbSK(numberOfParticipants: number): string {
    return numberOfParticipants < 16
      ? this.splitShareOptionLabelsMbSK[numberOfParticipants]
      : "as to a one-" + numberOfParticipants + "th interest";
  }

  static getDynamicValueInterest(numberOfParticipants: number): string {
    let splitShareOptionLabels: string[] = [
      "as to a 50% interest",
      "as to a one-third interest",
      "as to a 25% interest",
      "as to a 20% interest",
      "as to a one-sixth interest",
      "as to a one-seventh interest",
      "as to a one-eighth interest",
      "as to a one-ninth interest",
      "as to a 10% interest",
      "as to a one-eleventh interest",
      "as to a one-twelfth interest",
      "as to a one-thirteenth interest",
      "as to a one-fourteenth interest",
      "as to a one-fifteenth interest",
    ];
    return numberOfParticipants < 16
      ? splitShareOptionLabels[numberOfParticipants - 2]
      : "Each as to a one-" + numberOfParticipants + "th interest";
  }

  static splitShareOptionLabelsMbSK: string[] = [
    "as to an undivided _______ interest", //Same label when no participants are selected
    "as to an undivided _______ interest",
    "as to an undivided 50% interest",
    "as to an undivided one-third interest",
    "as to an undivided 25% interest",
    "as to an undivided 20% interest",
    "as to an undivided one-sixth interest",
    "as to an undivided one-seventh interest",
    "as to an undivided one-eighth interest",
    "as to an undivided one-ninth interest",
    "as to an undivided 10% interest",
    "as to an undivided one-eleventh interest",
    "as to an undivided one-twelfth interest",
    "as to an undivided one-thirteenth interest",
    "as to an undivided one-fourteenth interest",
    "as to an undivided one-fifteenth interest",
  ];

  static escapeSearchText(searchValue: string): string {
    if (searchValue) {
      searchValue = searchValue.split("_").join("\\_");
      searchValue = searchValue.split("|").join("\\|");
      searchValue = searchValue.split(",").join("\\,");
      //Encode URI component for any other special characters
      searchValue = encodeURIComponent(searchValue);
    }
    return searchValue;
  }

  static removeSpecialCharactersFromAddressSearch(text: string): string {
    try {
      let removeUrlEncoding: string = decodeURIComponent(text);
      return removeUrlEncoding.replace(/[^A-Za-z0-9!?&]/g, "");
    } catch (e) {
      //decodeUIComponent gives URIError if input is only %  since % is a escape character, it can't be on its own.
      return text.replace(/[^A-Za-z0-9!?&]/g, "");
    }
  }

  static appendContactStatusFilter(
    filter: string,
    clientStatusQuery: string
  ): string {
    let clientStatusFilter: string = "";
    if (clientStatusQuery && clientStatusQuery.length > 0) {
      switch (clientStatusQuery.toUpperCase()) {
        case "ALL":
          break;
        case "ACTIVE":
          clientStatusFilter +=
            "activeFlag_IN_" + Utils.escapeSearchText(`Y_n`) + "!YES";
          break;
        case "INACTIVE":
          clientStatusFilter +=
            "activeFlag_IN_" + Utils.escapeSearchText(`N_y`) + "!NO";
          break;
        default:
          break;
      }
    }

    if (clientStatusFilter.length > 0) {
      if (filter && filter.length > 0) {
        filter += "," + clientStatusFilter;
      } else {
        filter += clientStatusFilter;
      }
    }

    return filter;
  }
  static parseSort(sorting, sortType): string {
    let res: any[] = [];
    let multipleSorting: string = "";
    if (sorting != null) {
      if (sorting.indexOf(",") > -1) {
        res = sorting.split(",");
        for (let i = 0; i < res.length; i++) {
          if (i == res.length - 1) {
            multipleSorting = multipleSorting + res[i] + "|" + sortType;
          } else {
            multipleSorting = multipleSorting + res[i] + "|" + sortType + ",";
          }
        }
        return multipleSorting;
      } else {
        return sorting + "|" + sortType;
      }
    } else {
      return "";
    }
  }

  //As pipe character is not accepted by some versions of tomcat therefore encoding it if exists in the get URL
  static normalizeUrl(url: string): string {
    return url.replace("//", "/");
  }
  //As pipe character is not accepted by some versions of tomcat therefore encoding it if exists in the get URL
  static encodePipe(url: string): string {
    return url.split("|").join("%7C");
  }

  static addCommaToFilter(filter): string {
    if (filter != "") {
      filter += ",";
    }
    return filter;
  }

  static isNotNullorUndefined(obj: any): boolean {
    return obj != undefined && obj != null;
  }

  /*
   * Sometimes a promise may take too long to resolve or reject.
   * The timeoutPromise() method returns a promise that resolves or rejects dependding on the time we pass as a parameter, with the value or reason from that promise.
   * @param ms: Max time to wait in ms
   * @param promise: Promise Function
   * @returns Promise if resolved or error if rejected
   */
  static timeoutPromise = (ms: number, promise: Promise<any>): Promise<any> => {
    // Create a promise that rejects in <ms> milliseconds
    let timeout = new Promise((resolve, reject) => {
      let id = setTimeout(() => {
        clearTimeout(id);
        reject("Timed out in " + ms + "ms.");
      }, ms);
    });

    // Returns a race between our timeout and the passed in promise
    return Promise.race([promise, timeout]);
  };

  /**
   * Takes combined name field with LASTNAME, FIRSTNAME or FIRSTNAME [MIDDLENAME] LASTNAME format and parses names.
   *
   * @param {string} inputName
   * @returns {string[]} Array with LASTNAME, FIRSTNAME, MIDDLENAME
   */
  static parseLastFirstMiddleNames(inputName: string): string[] {
    let parsedNames: string[] = [];
    if (inputName) {
      if (inputName.includes(",")) {
        parsedNames = this.splitLastFirstAndMiddleName(inputName);
      } else if (inputName.includes(" ")) {
        let splitNames: string[] = inputName.split(" ");
        parsedNames.push(splitNames.pop()); //last name is last word

        if (splitNames.length > 0) {
          parsedNames.push(splitNames.shift()); //first name is first word
        }

        if (splitNames.length > 0) {
          parsedNames.push(splitNames.join(" ")); //middle name is any remaining words recombined with space
        }
      } else {
        parsedNames.push(inputName); //unable to split so returning as last name
      }
    }

    return parsedNames;
  }

  static splitLastFirstAndMiddleName(input: string): string[] {
    let arr: string[] = [];
    if (input != undefined && input != null && input.indexOf(",") > -1) {
      let splittedName = this.splitLastAndGivenName(input);

      if (splittedName.length > 0) {
        arr.push(splittedName[0]);
        if (splittedName.length > 1) {
          arr.push(splittedName[1].split(/\s(.+)/)[0]); //everything before the first space
          arr.push(splittedName[1].split(/\s(.+)/)[1]);
        }
      }
    }
    return arr;
  }

  /**
   * This method splits the input string into surname &  given names.
   *
   * @param {string} input: Input string can be in format of [SURNAME, FIRSTNAME MIDDLENAME] or [SURNAME, FIRSTNAME] or [SURNAME].
   * @returns {string[]}: It returns array with SURNAME at the first index and "FIRSTNAME MIDDLENAME" at second index
   */
  static splitLastAndGivenName(input: string): string[] {
    if (input != undefined && input != null) {
      return input.split(",").map((value) => {
        return value.trim();
      });
    } else {
      return [];
    }
  }

  static isIE() {
    const ua = window.navigator.userAgent; //Check the userAgent property of the window.navigator object
    const msie = ua.indexOf("MSIE "); // IE 10 or older
    const trident = ua.indexOf("Trident/"); //IE 11

    return msie > 0 || trident > 0;
  }

  static toMixedCase(text: string): string {
    if (!text) {
      return text;
    }

    return text
      .split(" ")
      .map((word) => {
        if (upperCaseWords.indexOf(word) > -1) {
          return word;
        } else if (lowerCaseWords.indexOf(word) > -1) {
          return word.toLocaleLowerCase();
        } else {
          return (
            word.charAt(0).toLocaleUpperCase() +
            word.slice(1).toLocaleLowerCase()
          );
        }
      })
      .join(" ");
  }

  static getUploadedFileFrontDescription(
    identificationTypeCode: string
  ): string {
    if (identificationTypeCode == CANDADIAN_DRIVER_LICENCE_ID) {
      //We treat the Canadian driver's license differently because of the existing file description for existing CIRFs
      return UploadedFileDescription.driverLicenseFront;
    } else if (identificationTypeCode == PASSPORT_CANADIAN_ID) {
      return UploadedFileDescription.passportCanadianId;
    } else {
      return (
        identificationTypeCode + " " + UploadedFileDescription.frontDescription
      );
    }
  }

  static getUploadedFileBackDescription(
    identificationTypeCode: string
  ): string {
    if (identificationTypeCode == CANDADIAN_DRIVER_LICENCE_ID) {
      //We treat the Canadian driver's license differently because of the existing file description for existing CIRFs
      return UploadedFileDescription.driverLicenseBack;
    } else {
      return (
        identificationTypeCode + " " + UploadedFileDescription.backDescription
      );
    }
  }

  static calculateDuration(startTime: number): string {
    const currentTime = performance.now();
    return (currentTime - startTime) / 1000 + " seconds.";
  }

  static createChangePassFormControl(): FormControl {
    return new FormControl(
      "",
      Validators.compose([
        // 1. Password Field is Required
        Validators.required,
        // 2. check whether the entered password has a number
        Utils.patternValidator(PasswordRegexConstant.ONE_NUMBER_CASE_REGEX, {
          hasNumber: true,
        }),
        // 3. check whether the entered password has special letter
        Utils.patternValidator(PasswordRegexConstant.ONE_UPPER_CASE_REGEX, {
          hasCapitalCase: true,
        }),
        // 4. check whether the entered password has upper case letter
        Utils.patternValidator(PasswordRegexConstant.ONE_SPECIAL_REGEX, {
          hasSpecial: true,
        }),
        // 5. check whether the entered password has consecutive
        Utils.patternValidator(PasswordRegexConstant.ALLOWED_CHARS_REGEX, {
          allowedChar: true,
        }),
        // 5. check whether the entered password has consecutive
        Utils.patternValidator(
          PasswordRegexConstant.CONSEC_CHAR_CHECK_REGEX,
          { hasConsecutive: true },
          true
        ),
        // 6. Has a minimum length of 8 characters
        Validators.minLength(8),
      ])
    );
  }

  static currentYear(): number {
    return new Date().getFullYear();
  }

  static toggleBackToTopBtnVisibility(
    scrollableDiv: ElementRef,
    backToTopBtn: ElementRef
  ): void {
    if (scrollableDiv.nativeElement.scrollTop > 20) {
      backToTopBtn.nativeElement.style.display = "block";
    } else {
      backToTopBtn.nativeElement.style.display = "none";
    }
  }

  static backToTop(scrollableDiv: ElementRef) {
    scrollableDiv.nativeElement.scrollTop = 0;
  }
}

export const PasswordRegexConstant = {
  ALLOWED_CHARS_REGEX:
    /^[A-Za-z0-9!"#\$%&'\(\)\*\+,\-\./:;<=>\?@\[\\\]\^_`\{\|\}~]{8,}$/,
  ONE_UPPER_CASE_REGEX: /[A-Z]+/,
  ONE_NUMBER_CASE_REGEX: /\d+/,
  ONE_SPECIAL_REGEX: /[!"#\$%&'\(\)\*\+,\-\./:;<=>\?@\[\\\]\^_`\{\|\}~]/,
  CONSEC_CHAR_CHECK_REGEX: /(.)\1{2,}/,
};
